import {
  createComponentAdmin,
  ModuleCategory,
  StringEnum,
  PublishesTo,
  SubscribesTo,
  type SchemaTypeHelper,
} from '@backstage-components/base';
import {GuestConsentInstructionSchema} from '@backstage-components/guest-consent-provider';
import {Type, type Static} from '@sinclair/typebox';
import {contentTranslations} from './content';
export const reactName = 'CookieBanner';
export const name = 'Cookie Banner';
export const cssVarNamespace = 'contentPrivacy';
export const description = reactName;
const category: ModuleCategory = 'preset';

export const schema = Type.Object({
  accentColor: Type.String({
    title: 'Accent Color',
    default: '#5363F2',
  }),
  theme: StringEnum(['dark', 'light'] as const, {
    title: 'Theme',
    default: 'dark',
  }),
  position: StringEnum(
    ['Bottom Left', 'Bottom Center', 'Bottom Right'] as const,
    {
      title: 'Banner Position',
      default: 'Bottom Left',
    }
  ),
  policyContent: Type.Object(
    {
      bannerTitle: Type.String({
        title: 'Banner Title',
        default: contentTranslations.en?.bannerTitle,
      }),
      bannerText: Type.String({
        title: 'Banner Text',
        default: contentTranslations.en?.bannerText,
      }),
      preferencesTitle: Type.String({
        title: 'Preferences Title',
        default: contentTranslations.en?.preferencesTitle,
      }),
      preferencesText: Type.String({
        title: 'Preferences Text',
        default: contentTranslations.en?.preferencesText,
      }),
    },
    {
      title: 'Privacy Banner Content',
      // Warning to not change content unless you know what you are doing
      description:
        'Content for the privacy banner and preferences dialog, this is for localization purposes only.',
    }
  ),
});

const uiSchema = {
  'ui:groups': {
    'ui:template': 'tabs',
    sections: [
      [
        'Properties',
        [
          {
            'ui:template': 'accordion',
            sections: [
              ['Appearance', ['accentColor', 'theme', 'position']],
              ['Content', ['policyContent']],
            ],
          },
        ],
      ],
      [
        'Styling',
        [
          {
            'ui:template': 'accordion',
            sections: [['Custom Styles', ['styleAttr']]],
          },
        ],
      ],
      ['Animations', []],
    ],
  },
  accentColor: {
    'ui:widget': 'color',
  },
  theme: {
    'ui:widget': 'LayoutWidget',
    'ui:options': {
      inline: true,
    },
  },
  policyContent: {
    bannerText: {
      'ui:widget': 'textarea',
      'ui:options': {
        rows: 4,
      },
    },
    preferencesText: {
      'ui:widget': 'textarea',
      'ui:options': {
        rows: 4,
      },
    },
  },
};

const defaultFieldData: Static<typeof schema> = {
  policyContent: {
    bannerTitle: schema.properties.policyContent.properties.bannerTitle.default,
    bannerText: schema.properties.policyContent.properties.bannerText.default,
    preferencesTitle:
      schema.properties.policyContent.properties.preferencesTitle.default,
    preferencesText:
      schema.properties.policyContent.properties.preferencesText.default,
  },
  theme: schema.properties.theme.default,
  accentColor: schema.properties.accentColor.default,
  position: schema.properties.position.default,
};

const instructions = Type.Union([
  ...GuestConsentInstructionSchema.anyOf,
  PublishesTo({
    topic: `${reactName}:on-customize`,
    description: 'User has clicked the customize button',
  }),
  PublishesTo({
    topic: `${reactName}:on-close-preferences`,
    description: 'User has closed the preferences dialog without saving',
  }),
  SubscribesTo({
    topic: `${reactName}:launch-customize-preferences`,
    description:
      'Launch the dialog to allow users to customize their content privacy preferences',
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: '5f671479-567b-4c26-a237-fa29148f8a9c',
  reactName,
  name,
  category,
  description,
  defaultFieldData,
  instructions,
  schema,
  slotConfiguration: {},
  slug: reactName,
  uiSchema,
  version: 1,
})
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
